import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { UserLayout } from '../../components/layout/UserLayout';
import { FocusedLayout } from '../../components/layout/FocusedLayout';
import { userAPI, statementAPI, publicAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import { formatDate, formatCurrency, convertAmount } from '../../utils/helpers';
import { useAuth } from '../../contexts/AuthContext';
import { Search, Download, FileText } from 'lucide-react';

export const WalletsPage = () => {
  const { user } = useAuth();
  const location = useLocation();
  const defaultCurrency = user?.default_currency || null;
  
  // Use FocusedLayout when coming from Super App
  const fromSuperApp = location.state?.fromSuperApp !== false;
  const Layout = fromSuperApp ? FocusedLayout : UserLayout;
  const [wallets, setWallets] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [hideZeroBalance, setHideZeroBalance] = useState(false);

  useEffect(() => {
    Promise.all([userAPI.getWallets(), userAPI.getTransactions(0, 100), publicAPI.getExchangeRates()])
      .then(([wRes, tRes, rRes]) => {
        setWallets(wRes.data);
        setTransactions(tRes.data);
        setRates(rRes.data);
      })
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  const downloadStatement = async (format) => {
    setDownloading(true);
    try {
      const { data } = format === 'pdf' 
        ? await statementAPI.downloadPDF()
        : await statementAPI.downloadCSV();
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `matrise-statement.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} statement downloaded`);
    } catch (error) {
      toast.error('Download failed');
    } finally {
      setDownloading(false);
    }
  };

  const filtered = transactions.filter(t => 
    !search || t.type?.toLowerCase().includes(search.toLowerCase()) || t.currency?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Layout title="My Wallets">
      <div className="space-y-6" data-testid="wallets-page">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-4xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>Wallets</h1>
            <p className="mt-2 text-muted-foreground">View your balances and transaction history</p>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="hideZero"
              checked={hideZeroBalance}
              onChange={(e) => setHideZeroBalance(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300"
            />
            <label htmlFor="hideZero" className="text-sm text-muted-foreground cursor-pointer">
              Hide zero balances
            </label>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {wallets
            .filter(w => !hideZeroBalance || w.balance > 0)
            .map(w => {
            const equiv = defaultCurrency && defaultCurrency !== w.currency
              ? convertAmount(w.balance, w.currency, defaultCurrency, rates)
              : null;
            return (
            <Card key={w.id} data-testid={`wallet-card-${w.currency}`}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="h-12 w-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold">
                    {w.currency.substring(0, 2)}
                  </div>
                  <Badge className={w.frozen ? 'bg-red-600' : w.locked ? 'bg-yellow-600' : 'bg-green-600'}>
                    {w.frozen ? 'Frozen' : w.locked ? 'Locked' : 'Active'}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">{w.currency} Balance</p>
                <p className="text-3xl font-semibold mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {formatCurrency(w.balance, w.currency)}
                </p>
                {equiv != null && (
                  <p className="text-sm text-muted-foreground mt-1" data-testid={`wallet-equiv-${w.currency}`}>
                    ≈ {equiv.toFixed(2)} {defaultCurrency}
                  </p>
                )}
              </CardContent>
            </Card>
            );
          })}
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <CardTitle>Transaction History</CardTitle>
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => downloadStatement('csv')}
                  disabled={downloading}
                  data-testid="download-csv-button"
                >
                  <Download className="h-3 w-3 mr-1" /> CSV
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => downloadStatement('pdf')}
                  disabled={downloading}
                  data-testid="download-pdf-button"
                >
                  <FileText className="h-3 w-3 mr-1" /> PDF
                </Button>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input className="pl-9 w-64" placeholder="Search transactions..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="tx-search" />
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? <p className="text-center py-8">Loading...</p> : filtered.length === 0 ? (
              <p className="text-center py-8 text-muted-foreground">No transactions yet</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Currency</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Reference</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map(t => (
                    <TableRow key={t.id}>
                      <TableCell className="text-xs">{formatDate(t.created_at)}</TableCell>
                      <TableCell className="capitalize">{t.type.replace('_', ' ')}</TableCell>
                      <TableCell className="font-semibold">{t.currency}</TableCell>
                      <TableCell>{t.amount}</TableCell>
                      <TableCell>
                        <Badge className={t.status === 'completed' || t.status === 'approved' ? 'bg-green-600' : t.status === 'pending' ? 'bg-yellow-600' : 'bg-gray-500'}>
                          {t.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">{t.reference || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};
